<script>
  import {
    Eye,
    EyeOff,
    GripHorizontal,
    Image as ImageIcon,
    LayoutDashboard,
    Maximize2,
    MousePointer2,
    MoveHorizontal,
    Palette,
    RotateCcw,
    Save,
    Ruler,
    SlidersHorizontal
  } from 'lucide-svelte';
  import SettingsCard from './shared/SettingsCard.svelte';
  import {
    MESSENGER_CONTENT_BACKGROUNDS,
    MESSENGER_LAYOUT_PRESETS,
    MESSENGER_SPLIT_THEMES,
    getDefaultMessengerLayoutSettings,
    messengerLayoutSettings,
    messengerLayoutSettingsStatus,
    resetMessengerLayoutStorage
  } from '$lib/messenger-layout-settings.js';

  const layoutOptions = Object.entries(MESSENGER_LAYOUT_PRESETS).map(([id, preset]) => ({ id, ...preset }));
  const splitThemeOptions = Object.entries(MESSENGER_SPLIT_THEMES).map(([id, theme]) => ({ id, ...theme }));
  const contentBackgroundOptions = Object.entries(MESSENGER_CONTENT_BACKGROUNDS).map(([id, background]) => ({
    id,
    ...background
  }));
  const gripPositionOptions = [
    { id: 'start', label: 'Đầu', icon: MoveHorizontal },
    { id: 'center', label: 'Giữa', icon: GripHorizontal },
    { id: 'end', label: 'Đuôi', icon: MoveHorizontal },
    { id: 'full', label: 'Full', icon: Maximize2 }
  ];
  const cursorOptions = [
    { id: 'auto', label: 'Auto' },
    { id: 'grab', label: 'Grab' },
    { id: 'col-resize', label: 'Col' },
    { id: 'row-resize', label: 'Row' },
    { id: 'ew-resize', label: 'EW' },
    { id: 'ns-resize', label: 'NS' }
  ];
  const splitterSizeOptions = [1, 2, 3, 4, 6, 8, 10, 12];
  const splitterHitSizeOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18, 20];
  const paneBorderSizeOptions = [0, 1, 2, 3, 4, 6, 8];
  const contentBackgroundFitOptions = [
    { id: 'cover', label: 'Phủ kín' },
    { id: 'contain', label: 'Vừa khung' },
    { id: 'repeat', label: 'Lặp ảnh' }
  ];
  const maxBackgroundImageBytes = 1.5 * 1024 * 1024;
  const checkboxClass = 'h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-100';
  const actionButtonClass =
    'inline-flex h-10 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60';

  let status = '';
  let statusTone = 'success';
  let backgroundFileInput;

  $: settings = $messengerLayoutSettings;
  $: syncState = $messengerLayoutSettingsStatus;
  $: syncBusy = syncState.loading || syncState.saving;
  $: systemScopeDisabled = syncBusy || !syncState.canManageSystem;
  $: resizeDisabled = !settings.resizeEnabled;
  $: gripDisabled = !settings.resizeEnabled || !settings.showResizeGrip;
  $: dividerColorDisabled = !settings.paneBorderEnabled && gripDisabled;
  $: resizeDependentClass = resizeDisabled ? 'opacity-60' : '';
  $: gripDependentClass = gripDisabled ? 'opacity-60' : '';
  $: borderDependentClass = settings.paneBorderEnabled ? '' : 'opacity-60';
  $: cursorPreview = settings.resizeCursor === 'auto' ? 'col-resize' : settings.resizeCursor;
  $: previewGripPosition =
    settings.gripPosition === 'full'
      ? 'top: 0; bottom: 0; height: auto; transform: translateX(-50%); border-radius: 0;'
      : settings.gripPosition === 'start'
        ? 'top: 12px; transform: translateX(-50%);'
        : settings.gripPosition === 'end'
          ? 'bottom: 12px; transform: translateX(-50%);'
          : 'top: 50%; transform: translate(-50%, -50%);';
  $: paneBorderStyle = `inset 0 0 0 ${settings.paneBorderEnabled ? settings.paneBorderWidth : 0}px ${settings.paneBorderColor}`;
  $: previewHitWidth = settings.splitterHitSize || 8;
  $: previewHitStyle = `left: calc(42% - ${previewHitWidth / 2}px); width: ${previewHitWidth}px; cursor: ${cursorPreview}; background: ${settings.gapColor};`;
  $: previewGripStyle = `left: 50%; width: ${settings.dividerSize}px; height: 46px; background: ${settings.dividerColor}; ${previewGripPosition}`;
  $: customBackgroundActive = settings.contentBackgroundMode === 'custom' && Boolean(settings.contentBackgroundImage);
  $: contentBackgroundPreview = customBackgroundActive
    ? `linear-gradient(rgba(248, 250, 252, 0.18), rgba(248, 250, 252, 0.18)), url("${settings.contentBackgroundImage}")`
    : MESSENGER_CONTENT_BACKGROUNDS[settings.contentBackgroundPreset]?.background ||
      MESSENGER_CONTENT_BACKGROUNDS.calm.background;
  $: contentBackgroundPreviewSize = customBackgroundActive
    ? settings.contentBackgroundFit === 'repeat'
      ? 'auto'
      : settings.contentBackgroundFit
    : MESSENGER_CONTENT_BACKGROUNDS[settings.contentBackgroundPreset]?.size || 'cover';
  $: contentBackgroundPreviewRepeat =
    customBackgroundActive && settings.contentBackgroundFit === 'repeat' ? 'repeat' : 'no-repeat';

  function savePatch(patch, message = 'Đã lưu Layout Messenger.') {
    messengerLayoutSettings.update((current) => ({
      ...current,
      ...patch
    }));
    statusTone = 'success';
    status = message;
  }

  async function persistUserLayout(message = 'Đã lưu Layout Messenger.') {
    try {
      await messengerLayoutSettings.save('user');
      statusTone = 'success';
      status = message;
    } catch (error) {
      statusTone = 'error';
      status = error?.message || 'Không thể lưu Layout Messenger.';
    }
  }

  function changeLayout(uiLayout) {
    savePatch({ uiLayout, layoutVersion: Date.now() }, 'Đã đổi bố cục.');
  }

  function toggleResize(event) {
    const resizeEnabled = event.currentTarget.checked;
    savePatch(
      {
        resizeEnabled,
        showResizeGrip: resizeEnabled
      },
      resizeEnabled ? 'Đã bật resize.' : 'Đã khóa resize.'
    );
  }

  function toggleResizeGrip(event) {
    savePatch(
      { showResizeGrip: event.currentTarget.checked },
      event.currentTarget.checked ? 'Đã hiện grip.' : 'Đã ẩn grip.'
    );
  }

  function togglePersistResizableLayout(event) {
    savePatch(
      { persistResizableLayout: event.currentTarget.checked },
      event.currentTarget.checked ? 'Đã lưu kích thước.' : 'Không lưu kích thước.'
    );
  }

  function togglePaneBorder(event) {
    savePatch(
      { paneBorderEnabled: event.currentTarget.checked },
      event.currentTarget.checked ? 'Đã bật viền slot.' : 'Đã tắt viền slot.'
    );
  }

  function applySplitTheme(themeId) {
    const theme = MESSENGER_SPLIT_THEMES[themeId];
    if (!theme) return;

    savePatch(
      {
        splitTheme: themeId,
        gapColor: theme.gapColor,
        dividerColor: theme.dividerColor,
        dividerHoverColor: theme.dividerHoverColor,
        dividerSize: theme.dividerSize,
        splitterHitSize: theme.splitterHitSize,
        paneBorderColor: theme.paneBorderColor,
        paneBorderWidth: theme.paneBorderWidth,
        paneBorderEnabled: true
      },
      'Đã áp dụng theme split panes.'
    );
  }

  function updateLayoutColor(color) {
    savePatch(
      {
        dividerColor: color,
        paneBorderColor: color
      },
      'Đã đổi màu layout.'
    );
  }

  function updateStyle(patch) {
    savePatch(patch, 'Đã cập nhật style.');
  }

  async function saveLayoutScope(scope) {
    try {
      await messengerLayoutSettings.save(scope);
      statusTone = 'success';
      status = scope === 'system' ? 'Đã lưu System.' : 'Đã lưu User.';
    } catch (error) {
      statusTone = 'error';
      status = error?.message || 'Không thể lưu Layout Messenger.';
    }
  }

  async function applySystemLayout() {
    try {
      const response = await messengerLayoutSettings.applySystemToAllUsers();
      const cleared = response?.clearedUserOverrides || 0;
      statusTone = 'success';
      status = cleared
        ? `Đã áp dụng System toàn hệ thống, xoá ${cleared} cấu hình User.`
        : 'Đã áp dụng System toàn hệ thống.';
    } catch (error) {
      statusTone = 'error';
      status = error?.message || 'Không thể áp dụng System toàn hệ thống.';
    }
  }

  async function resetLayoutScope(scope) {
    try {
      resetMessengerLayoutStorage();
      await messengerLayoutSettings.resetScope(scope);
      statusTone = 'success';
      status = scope === 'system' ? 'Đã reset System.' : 'Đã reset User.';
    } catch (error) {
      statusTone = 'error';
      status = error?.message || 'Không thể reset Layout Messenger.';
    }
  }

  function applyContentBackgroundPreset(backgroundId) {
    if (!MESSENGER_CONTENT_BACKGROUNDS[backgroundId]) return;

    if (backgroundFileInput) backgroundFileInput.value = '';
    savePatch(
      {
        contentBackgroundMode: 'preset',
        contentBackgroundPreset: backgroundId,
        contentBackgroundImage: ''
      },
      'Đã đổi ảnh nền.'
    );
    persistUserLayout('Đã lưu ảnh nền.');
  }

  function pickContentBackgroundImage() {
    backgroundFileInput?.click();
  }

  function handleContentBackgroundUpload(event) {
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type)) {
      statusTone = 'error';
      status = 'Chỉ hỗ trợ PNG, JPG, WebP hoặc GIF.';
      event.currentTarget.value = '';
      return;
    }

    if (file.size > maxBackgroundImageBytes) {
      statusTone = 'error';
      status = 'Ảnh nền tối đa 1.5MB để tránh lag.';
      event.currentTarget.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const image = String(reader.result || '');
      if (!image.startsWith('data:image/')) {
        statusTone = 'error';
        status = 'Không đọc được ảnh nền.';
        return;
      }

      savePatch(
        {
          contentBackgroundMode: 'custom',
          contentBackgroundImage: image
        },
        'Đã chọn ảnh nền, đang lưu...'
      );
      persistUserLayout('Đã lưu ảnh nền.');
    };
    reader.onerror = () => {
      statusTone = 'error';
      status = 'Không đọc được ảnh nền.';
    };
    reader.readAsDataURL(file);
  }

  function resetContentBackground() {
    const defaults = getDefaultMessengerLayoutSettings();
    if (backgroundFileInput) backgroundFileInput.value = '';
    savePatch(
      {
        contentBackgroundMode: defaults.contentBackgroundMode,
        contentBackgroundPreset: defaults.contentBackgroundPreset,
        contentBackgroundImage: defaults.contentBackgroundImage,
        contentBackgroundFit: defaults.contentBackgroundFit
      },
      'Đã reset ảnh nền.'
    );
    persistUserLayout('Đã reset ảnh nền.');
  }

  function resetLayoutSize() {
    const removed = resetMessengerLayoutStorage();
    messengerLayoutSettings.update((current) => ({
      ...current,
      layoutVersion: Date.now()
    }));
    statusTone = 'success';
    status = removed ? `Đã reset ${removed} layout.` : 'Đã reset layout.';
  }

  function restoreDefaultLayout() {
    resetMessengerLayoutStorage();
    messengerLayoutSettings.set({
      ...getDefaultMessengerLayoutSettings(),
      layoutVersion: Date.now()
    });
    statusTone = 'success';
    status = 'Đã về mặc định.';
  }
</script>

<section class="min-h-full bg-slate-100">
  <div class="mx-auto max-w-7xl space-y-4 p-4 lg:p-6">
    {#if status}
      <div
        class="rounded-xl border px-4 py-3 text-sm font-medium {statusTone === 'error'
          ? 'border-rose-200 bg-rose-50 text-rose-700'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700'}">
        {status}
      </div>
    {/if}

    <SettingsCard title="Lưu cấu hình">
      <span slot="actions" class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
        <Save size={18} />
      </span>

      <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div class="rounded-2xl border border-slate-200 bg-white p-4">
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-slate-900">User</p>
              <p class="text-xs font-medium text-slate-500">Theo tài khoản hiện tại</p>
            </div>
            <span class="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
              {syncState.user ? 'Đã lưu' : 'Chưa có'}
            </span>
          </div>
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={syncBusy}
              class={`${actionButtonClass} border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
              on:click={() => saveLayoutScope('user')}>
              Lưu User
            </button>
            <button
              type="button"
              disabled={syncBusy}
              class={`${actionButtonClass} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}
              on:click={() => resetLayoutScope('user')}>
              Reset User
            </button>
          </div>
        </div>

        <div class="rounded-2xl border border-slate-200 bg-white p-4 {systemScopeDisabled ? 'opacity-60' : ''}">
          <div class="flex items-center justify-between gap-3">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-slate-900">System</p>
              <p class="text-xs font-medium text-slate-500">Áp dụng cho tất cả user</p>
            </div>
            <span class="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
              {syncState.canManageSystem ? (syncState.system ? 'Đã có' : 'Chưa có') : 'Chỉ đọc'}
            </span>
          </div>
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={systemScopeDisabled}
              class={`${actionButtonClass} border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
              on:click={applySystemLayout}>
              Áp dụng toàn hệ thống
            </button>
            <button
              type="button"
              disabled={systemScopeDisabled}
              class={`${actionButtonClass} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}
              on:click={() => resetLayoutScope('system')}>
              Reset System
            </button>
          </div>
        </div>
      </div>

      {#if syncState.loading || syncState.saving || syncState.error}
        <p class="mt-3 text-sm font-medium {syncState.error ? 'text-rose-600' : 'text-slate-500'}">
          {syncState.error || (syncState.saving ? 'Đang lưu...' : 'Đang tải...')}
        </p>
      {/if}
    </SettingsCard>

    <SettingsCard title="Ảnh nền nội dung Messenger">
      <span slot="actions" class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
        <ImageIcon size={18} />
      </span>

      <div class="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {#each contentBackgroundOptions as option}
          <button
            type="button"
            class="rounded-2xl border p-3 text-left transition {settings.contentBackgroundMode === 'preset' &&
            settings.contentBackgroundPreset === option.id
              ? 'border-blue-300 bg-blue-50 shadow-sm'
              : 'border-slate-200 bg-white hover:bg-slate-50'}"
            aria-pressed={settings.contentBackgroundMode === 'preset' && settings.contentBackgroundPreset === option.id}
            on:click={() => applyContentBackgroundPreset(option.id)}>
            <span class="text-sm font-semibold text-slate-900">{option.label}</span>
            <span
              class="mt-3 block h-16 rounded-xl border border-slate-200"
              style={`background: ${option.background}; background-size: ${option.size}; background-position: ${option.position}; background-repeat: ${option.repeat};`}>
            </span>
          </button>
        {/each}
      </div>

      <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_180px]">
        <div class="rounded-2xl border border-slate-200 bg-white p-3">
          <div
            class="h-24 rounded-xl border border-slate-200"
            style={`background: ${contentBackgroundPreview}; background-size: ${contentBackgroundPreviewSize}; background-position: center; background-repeat: ${contentBackgroundPreviewRepeat};`}>
          </div>
          <div class="mt-3 flex flex-wrap gap-2">
            <input
              bind:this={backgroundFileInput}
              class="hidden"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              on:change={handleContentBackgroundUpload} />
            <button
              type="button"
              class={`${actionButtonClass} border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
              on:click={pickContentBackgroundImage}>
              Chọn ảnh
            </button>
            <button
              type="button"
              class={`${actionButtonClass} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}
              on:click={resetContentBackground}>
              Reset nền
            </button>
          </div>
          <p class="mt-2 text-xs font-medium text-slate-500">PNG/JPG/WebP/GIF, tối đa 1.5MB.</p>
        </div>

        <label class="rounded-2xl border border-slate-200 bg-white p-3 {customBackgroundActive ? '' : 'opacity-60'}">
          <span class="mb-2 block text-sm font-semibold text-slate-800">Hiển thị ảnh</span>
          <select
            value={settings.contentBackgroundFit}
            disabled={!customBackgroundActive}
            class="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50"
            on:change={(event) => updateStyle({ contentBackgroundFit: event.currentTarget.value })}>
            {#each contentBackgroundFitOptions as option}
              <option value={option.id}>{option.label}</option>
            {/each}
          </select>
        </label>
      </div>
    </SettingsCard>

    <SettingsCard title="Bố cục">
      <span slot="actions" class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
        <LayoutDashboard size={18} />
      </span>

      <div class="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-6">
        {#each layoutOptions as option}
          <button
            type="button"
            class="min-w-0 rounded-2xl border p-3 text-left transition {settings.uiLayout === option.id
              ? 'border-blue-300 bg-blue-50 shadow-sm'
              : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}"
            aria-pressed={settings.uiLayout === option.id}
            title={option.description}
            on:click={() => changeLayout(option.id)}>
            <span class="flex items-center justify-between gap-2">
              <span class="truncate text-sm font-semibold text-slate-900">{option.label}</span>
              <span class="rounded-full bg-white/80 px-2 py-0.5 text-[11px] font-semibold text-slate-500">
                {option.target}
              </span>
            </span>
            <span class="mt-3 grid grid-cols-[1fr_2fr_1fr] gap-1 overflow-hidden rounded-xl bg-white/80 p-1">
              <span
                class="h-9 rounded-lg bg-slate-200"
                style={`width: ${Math.max(42, option.leftSize * 2)}%; max-width: 100%;`}>
              </span>
              <span class="h-9 rounded-lg bg-blue-200"></span>
              <span
                class="h-9 rounded-lg {option.rightSize ? 'bg-slate-300' : 'bg-slate-100'}"
                style={`width: ${Math.max(12, option.rightSize * 2)}%; max-width: 100%;`}>
              </span>
            </span>
          </button>
        {/each}
      </div>
    </SettingsCard>

    <div class="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
      <SettingsCard title="Resize">
        <span
          slot="actions"
          class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
          <SlidersHorizontal size={18} />
        </span>

        <div class="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <label class="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <input type="checkbox" checked={settings.resizeEnabled} class={checkboxClass} on:change={toggleResize} />
            <SlidersHorizontal size={18} class="text-blue-700" />
            <span class="min-w-0 text-sm font-semibold text-slate-900">Cho phép resize</span>
          </label>

          <label
            class="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 {resizeDependentClass}">
            <input
              type="checkbox"
              checked={settings.showResizeGrip}
              disabled={!settings.resizeEnabled}
              class={checkboxClass}
              on:change={toggleResizeGrip} />
            {#if settings.showResizeGrip}
              <Eye size={18} class="text-blue-700" />
            {:else}
              <EyeOff size={18} class="text-slate-500" />
            {/if}
            <span class="min-w-0 text-sm font-semibold text-slate-900">Hiển thị grip</span>
          </label>

          <label
            class="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 {resizeDependentClass}">
            <input
              type="checkbox"
              checked={settings.persistResizableLayout}
              disabled={!settings.resizeEnabled}
              class={checkboxClass}
              on:change={togglePersistResizableLayout} />
            <Ruler size={18} class="text-blue-700" />
            <span class="min-w-0 text-sm font-semibold text-slate-900">Lưu kích thước</span>
          </label>
        </div>

        <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div class={gripDependentClass}>
            <div class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <GripHorizontal size={17} />
              Vị trí grip
            </div>
            <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {#each gripPositionOptions as option}
                <button
                  type="button"
                  disabled={gripDisabled}
                  class="inline-flex h-10 items-center justify-center gap-2 rounded-xl border text-sm font-semibold transition disabled:cursor-not-allowed {settings.gripPosition ===
                  option.id
                    ? 'border-blue-300 bg-blue-50 text-blue-700'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}"
                  on:click={() => updateStyle({ gripPosition: option.id })}>
                  <svelte:component this={option.icon} size={16} />
                  {option.label}
                </button>
              {/each}
            </div>
          </div>

          <label class="rounded-xl border border-slate-200 bg-white p-3 {resizeDependentClass}">
            <span class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <MousePointer2 size={16} />
              Cursor
            </span>
            <select
              value={settings.resizeCursor}
              disabled={resizeDisabled}
              class="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50"
              on:change={(event) => updateStyle({ resizeCursor: event.currentTarget.value })}>
              {#each cursorOptions as option}
                <option value={option.id}>{option.label}</option>
              {/each}
            </select>
          </label>
        </div>
      </SettingsCard>

      <SettingsCard title="Preview">
        <span
          slot="actions"
          class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
          <Maximize2 size={18} />
        </span>

        <div
          class="relative h-52 overflow-hidden rounded-xl border border-slate-200"
          style={`background: ${settings.gapColor};`}>
          <div class="absolute left-0 top-0 h-8 w-full bg-slate-50" style={`box-shadow: ${paneBorderStyle};`}></div>
          <div class="absolute bottom-0 left-0 h-8 w-full bg-slate-50" style={`box-shadow: ${paneBorderStyle};`}></div>
          <div class="absolute bottom-8 left-0 top-8 w-[42%] bg-slate-100" style={`box-shadow: ${paneBorderStyle};`}>
          </div>
          <div class="absolute bottom-8 left-[42%] top-8 w-[40%] bg-blue-50" style={`box-shadow: ${paneBorderStyle};`}>
          </div>
          <div class="absolute bottom-8 right-0 top-8 w-[18%] bg-slate-50" style={`box-shadow: ${paneBorderStyle};`}>
          </div>
          <span class="absolute bottom-8 top-8" style={previewHitStyle}>
            {#if settings.showResizeGrip}
              <span class="absolute rounded-full shadow-sm" style={previewGripStyle}></span>
            {/if}
          </span>
        </div>
      </SettingsCard>
    </div>

    <SettingsCard title="Theme">
      <span slot="actions" class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
        <Palette size={18} />
      </span>

      <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {#each splitThemeOptions as option}
          <button
            type="button"
            class="rounded-2xl border p-3 text-left transition {settings.splitTheme === option.id
              ? 'border-blue-300 bg-blue-50 shadow-sm'
              : 'border-slate-200 bg-white hover:bg-slate-50'}"
            title={option.description}
            on:click={() => applySplitTheme(option.id)}>
            <span class="flex items-center justify-between gap-2">
              <span class="text-sm font-semibold text-slate-900">{option.label}</span>
              <span
                class="h-4 w-4 rounded-full border border-white shadow-sm"
                style={`background: ${option.gapColor};`}>
              </span>
            </span>
            <span
              class="mt-3 grid grid-cols-[1fr_6px_1fr] overflow-hidden rounded-xl border"
              style={`border-color: ${option.paneBorderColor}; background: ${option.gapColor};`}>
              <span class="h-10 bg-white/80"></span>
              <span class="h-10" style={`background: ${option.dividerColor};`}></span>
              <span class="h-10 bg-white/80"></span>
            </span>
          </button>
        {/each}
      </div>
    </SettingsCard>

    <SettingsCard title="Viền và gap">
      <span slot="actions" class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
        <Palette size={18} />
      </span>

      <div class="grid grid-cols-1 gap-4 lg:grid-cols-6">
        <label class="rounded-xl border border-slate-200 bg-white p-3">
          <span class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Palette size={16} />
            Màu gap
          </span>
          <input
            type="color"
            value={settings.gapColor}
            class="h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-white p-1"
            on:change={(event) => updateStyle({ gapColor: event.currentTarget.value })} />
        </label>

        <label class="rounded-xl border border-slate-200 bg-white p-3 {dividerColorDisabled ? 'opacity-60' : ''}">
          <span class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Palette size={16} />
            Màu viền
          </span>
          <input
            type="color"
            value={settings.dividerColor}
            disabled={dividerColorDisabled}
            class="h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-white p-1 disabled:cursor-not-allowed"
            on:change={(event) => updateLayoutColor(event.currentTarget.value)} />
        </label>

        <label class="rounded-xl border border-slate-200 bg-white p-3 {resizeDependentClass}">
          <span class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Palette size={16} />
            Hover splitter
          </span>
          <input
            type="color"
            value={settings.dividerHoverColor}
            disabled={resizeDisabled}
            class="h-10 w-full cursor-pointer rounded-lg border border-slate-200 bg-white p-1 disabled:cursor-not-allowed"
            on:change={(event) => updateStyle({ dividerHoverColor: event.currentTarget.value })} />
        </label>

        <label class="rounded-xl border border-slate-200 bg-white p-3 {gripDependentClass}">
          <span class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <SlidersHorizontal size={16} />
            Dày splitter
          </span>
          <select
            value={settings.dividerSize}
            disabled={gripDisabled}
            class="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50"
            on:change={(event) => updateStyle({ dividerSize: Number(event.currentTarget.value) })}>
            {#each splitterSizeOptions as size}
              <option value={size}>{size}px</option>
            {/each}
          </select>
        </label>

        <label class="rounded-xl border border-slate-200 bg-white p-3 {resizeDependentClass}">
          <span class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <MousePointer2 size={16} />
            Vùng kéo
          </span>
          <select
            value={settings.splitterHitSize}
            disabled={resizeDisabled}
            class="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50"
            on:change={(event) => updateStyle({ splitterHitSize: Number(event.currentTarget.value) })}>
            {#each splitterHitSizeOptions as size}
              <option value={size}>{size}px</option>
            {/each}
          </select>
        </label>

        <label class="rounded-xl border border-slate-200 bg-white p-3 {borderDependentClass}">
          <span class="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Ruler size={16} />
            Dày viền slot
          </span>
          <select
            value={settings.paneBorderWidth}
            disabled={!settings.paneBorderEnabled}
            class="h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-slate-50"
            on:change={(event) => updateStyle({ paneBorderWidth: Number(event.currentTarget.value) })}>
            {#each paneBorderSizeOptions as size}
              <option value={size}>{size}px</option>
            {/each}
          </select>
        </label>
      </div>

      <label
        class="mt-4 flex w-fit min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <input
          type="checkbox"
          checked={settings.paneBorderEnabled}
          class={checkboxClass}
          on:change={togglePaneBorder} />
        <span class="text-sm font-semibold text-slate-900">Bật viền cho tất cả slot</span>
      </label>
    </SettingsCard>

    <SettingsCard title="Reset">
      <span
        slot="actions"
        class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-amber-50 text-amber-700">
        <RotateCcw size={18} />
      </span>

      <div class="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          class={`${actionButtonClass} border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100`}
          on:click={resetLayoutSize}>
          <RotateCcw size={16} />
          Reset kích thước
        </button>

        <button
          type="button"
          class={`${actionButtonClass} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}
          on:click={restoreDefaultLayout}>
          Mặc định hệ thống
        </button>
      </div>
    </SettingsCard>
  </div>
</section>
